import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { settingsApi, uploadApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  Save, Loader2, MapPin, Clock, Home, Sparkles, Calendar, Star,
  Heart, Users, CheckCircle, Target, Shield, Zap, ArrowRight,
  FileText, Phone, ChevronDown, ChevronUp, Plus, Trash2, Upload,
  ZoomIn, ZoomOut, RotateCw, Monitor, Laptop, Video, Wifi, Globe,
  type LucideIcon,
} from "lucide-react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { consultorios as defaultConsultorios } from "@/data/consultorios";

// ── Icon map ───────────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, LucideIcon> = {
  Clock, Home, Sparkles, Calendar, Star, Heart, Users,
  CheckCircle, Target, Shield, Zap, ArrowRight, FileText, MapPin, Phone,
  Monitor, Laptop, Video, Wifi, Globe,
};
const ICON_NAMES = Object.keys(ICON_MAP);

// ── Shared UI ──────────────────────────────────────────────────────────────────
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength, type = "text" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number; type?: string;
}) {
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number;
}) {
  return (
    <textarea
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none"
    />
  );
}

function SectionHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
    </div>
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 mt-2">
      <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
      <button
        onClick={onSave} disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

// ── Icon picker ────────────────────────────────────────────────────────────────
function IconPickerInline({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const Icon = ICON_MAP[value] || Star;

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
          open ? "border-brand-primary bg-brand-primary/8" : "border-gray-200 bg-white hover:border-brand-primary/50"
        }`}
        title={`Ícone: ${value}`}
      >
        <Icon className="w-4 h-4 text-brand-primary" />
      </button>
      {open && createPortal(
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-56"
        >
          <div className="grid grid-cols-5 gap-1">
            {ICON_NAMES.map(name => {
              const Ic = ICON_MAP[name];
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => { onChange(name); setOpen(false); }}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                    value === name
                      ? "bg-brand-primary text-white"
                      : "text-gray-500 hover:bg-brand-primary/10 hover:text-brand-primary"
                  }`}
                >
                  <Ic className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── StringList editor (horarios, transporte, servicos, destaces) ───────────────
function StringListEditor({
  label, items, onChange,
}: { label: string; items: string[]; onChange: (items: string[]) => void }) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...items, t]);
    setDraft('');
  };

  const edit = (i: number, v: string) => {
    const arr = [...items];
    arr[i] = v;
    onChange(arr);
  };

  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label} ({items.length})</label>
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text" value={item} onChange={e => edit(i, e.target.value)} maxLength={200}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
          />
          <button onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text" value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`Adicionar ${label.toLowerCase()}...`} maxLength={200}
          className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
        />
        <button onClick={add} className="flex items-center gap-1 bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
    </div>
  );
}

// ── Canvas crop helper ─────────────────────────────────────────────────────────
async function getCroppedBlob(imageSrc: string, pixelCrop: Area, rotation = 0): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = imageSrc;
  });
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = safeArea; canvas.height = safeArea;
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);
  canvas.width = pixelCrop.width; canvas.height = pixelCrop.height;
  ctx.putImageData(data, Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x), Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y));
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.92);
  });
}

// ── Photo gallery editor (upload + crop + reorder) ─────────────────────────────
function PhotoGalleryEditor({ photos, onChange }: { photos: string[]; onChange: (urls: string[]) => void }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropOpen, setCropOpen]     = useState(false);
  const [imageSrc, setImageSrc]     = useState<string | null>(null);
  const [crop, setCrop]             = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom]             = useState(1);
  const [rotation, setRotation]     = useState(0);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [uploading, setUploading]   = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setCroppedArea(pixels), []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedArea) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedArea, rotation);
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      const url = await uploadApi.image(file);
      onChange([...photos, url]);
      setCropOpen(false); setImageSrc(null);
      toast({ title: 'Foto adicionada!' });
    } catch {
      toast({ title: 'Erro ao enviar foto', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const remove = (i: number) => onChange(photos.filter((_, idx) => idx !== i));
  const moveLeft  = (i: number) => { if (i === 0) return; const a = [...photos]; [a[i-1], a[i]] = [a[i], a[i-1]]; onChange(a); };
  const moveRight = (i: number) => { if (i === photos.length - 1) return; const a = [...photos]; [a[i], a[i+1]] = [a[i+1], a[i]]; onChange(a); };

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Fotos ({photos.length})</label>

      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((url, i) => (
            <div key={i} className="relative group rounded-xl overflow-hidden aspect-square bg-gray-100">
              <img src={url} alt={`Foto ${i+1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                <button type="button" onClick={() => remove(i)} className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-lg transition-colors">
                  <Trash2 className="w-3 h-3" /> Remover
                </button>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveLeft(i)} disabled={i === 0} className="bg-white/80 hover:bg-white text-gray-700 text-xs font-bold px-2 py-1 rounded-lg disabled:opacity-30">←</button>
                  <button type="button" onClick={() => moveRight(i)} disabled={i === photos.length - 1} className="bg-white/80 hover:bg-white text-gray-700 text-xs font-bold px-2 py-1 rounded-lg disabled:opacity-30">→</button>
                </div>
              </div>
              {i === 0 && <span className="absolute top-1.5 left-1.5 bg-brand-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">Capa</span>}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-brand-primary/30 hover:border-brand-primary/60 text-brand-primary text-sm font-medium rounded-xl py-3 transition-colors"
      >
        <Upload className="w-4 h-4" /> Adicionar foto
      </button>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* Crop modal */}
      {cropOpen && imageSrc && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setCropOpen(false); setImageSrc(null); }} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-sm">Ajustar foto</h3>
              <p className="text-xs text-gray-400 mt-0.5">Arraste para posicionar · zoom para ajustar</p>
            </div>
            <div className="relative w-full bg-gray-900" style={{ height: 300 }}>
              <Cropper image={imageSrc} crop={crop} zoom={zoom} rotation={rotation} aspect={1} showGrid={false}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <div className="px-5 py-4 space-y-3 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-gray-400 shrink-0" />
                <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={e => setZoom(Number(e.target.value))} className="flex-1 accent-brand-primary h-1.5 rounded-full cursor-pointer" />
                <ZoomIn className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Rotação: {rotation}°</span>
                <button type="button" onClick={() => setRotation(r => (r + 90) % 360)} className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-brand-primary bg-white border border-gray-200 px-3 py-1.5 rounded-lg transition-colors">
                  <RotateCw className="w-3.5 h-3.5" /> Girar 90°
                </button>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={handleConfirm} disabled={uploading} className="flex-1 flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</> : 'Confirmar'}
                </button>
                <button type="button" onClick={() => { setCropOpen(false); setImageSrc(null); }} disabled={uploading} className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface HeroData {
  label: string;
  labelIcon: string;
  title: string;
  subtitle: string;
  badges: { label: string; icon: string }[];
}

interface ConsultorioItem {
  id: number;
  consultorioId: string;
  name: string;
  address: string;
  city: string;
  cep: string;
  complement: string;
  phone: string;
  googleMapsUrl: string;
  agendamentoUrl: string;
  badgeText: string;
  horarios: string[];
  transporte: string[];
  servicos: string[];
  destaces: string[];
  photos: string[];
}

interface OnlineData {
  headerIcon: string;
  title: string;
  desc: string;
  buttonText: string;
  buttonUrl: string;
  features: { icon: string; title: string; desc: string }[];
}

interface StatItem { value: string; label: string }
interface StatsData {
  sectionTitle: string;
  sectionSubtitle: string;
  stats: StatItem[];
}

// ── Defaults ───────────────────────────────────────────────────────────────────
const DEFAULT_HERO: HeroData = {
  label: "3 Localizações em São Paulo",
  labelIcon: "MapPin",
  title: "Nossos Consultórios",
  subtitle: "Atendimento de qualidade em locais estratégicos. Escolha o consultório mais próximo de você.",
  badges: [
    { label: "Paulista · Mogi · Suzano", icon: "MapPin" },
    { label: "5.0 no Google",            icon: "Star" },
    { label: "CRN/3-63492",              icon: "Shield" },
  ],
};

const DEFAULT_CONSULTORIOS: ConsultorioItem[] = defaultConsultorios.map(c => ({
  id: c.id,
  consultorioId: c.consultorioId,
  name: c.name,
  address: c.address,
  city: c.city,
  cep: c.cep,
  complement: c.complement,
  phone: c.phone,
  googleMapsUrl: c.googleMapsUrl,
  agendamentoUrl: "",
  badgeText: "Verificado",
  horarios: c.horarios,
  transporte: c.transporte,
  servicos: c.servicos,
  destaces: c.destaces,
  photos: c.photos ?? [],
}));

const DEFAULT_ONLINE: OnlineData = {
  headerIcon: "Monitor",
  title: "Consulta Online",
  desc: "Não pode se deslocar? Atendimento de qualidade onde você estiver, com a mesma dedicação da consulta presencial.",
  buttonText: "Agendar Online",
  buttonUrl: "",
  features: [
    { icon: "Clock",     title: "Horários Flexíveis",  desc: "Agendamento conforme sua disponibilidade, inclusive noturno e fins de semana." },
    { icon: "Home",      title: "Conforto de Casa",    desc: "Atendimento completo sem sair de onde você estiver, com total privacidade." },
    { icon: "Sparkles",  title: "Mesma Qualidade",     desc: "Consulta personalizada com o mesmo cuidado e atenção do atendimento presencial." },
  ],
};

const DEFAULT_STATS: StatsData = {
  sectionTitle: "Números que Falam por Si",
  sectionSubtitle: "A confiança dos nossos pacientes em números",
  stats: [
    { value: "25+", label: "Avaliações Google" },
    { value: "5.0", label: "Média de Estrelas" },
    { value: "3",   label: "Localizações" },
    { value: "50+", label: "Pacientes Atendidos" },
  ],
};

// ── Tab 1: Cabeçalho ───────────────────────────────────────────────────────────
function CabecalhoTab() {
  const { toast } = useToast();
  const [data, setData]   = useState<HeroData>(DEFAULT_HERO);
  const [badgeDraft, setBadgeDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'consultorios_hero')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_HERO, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof HeroData>(key: K, val: HeroData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const addBadge = () => {
    const t = badgeDraft.trim();
    if (!t) return;
    upd('badges', [...data.badges, { label: t, icon: 'Star' }]);
    setBadgeDraft('');
  };

  const editBadge = (i: number, patch: Partial<{ label: string; icon: string }>) => {
    const arr = [...data.badges];
    arr[i] = { ...arr[i], ...patch };
    upd('badges', arr);
  };

  const removeBadge = (i: number) => upd('badges', data.badges.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('consultorios_hero', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={MapPin} title="Cabeçalho da Página" description="Seção hero no topo da página /consultorios" />

      <Field label="Pílula / Label">
        <div className="flex items-center gap-2">
          <IconPickerInline value={data.labelIcon ?? 'MapPin'} onChange={v => upd('labelIcon', v)} />
          <TextInput value={data.label} onChange={v => upd('label', v)} placeholder="3 Localizações em São Paulo" maxLength={60} />
        </div>
      </Field>
      <Field label="Título">
        <TextInput value={data.title} onChange={v => upd('title', v)} placeholder="Nossos Consultórios" maxLength={100} />
      </Field>
      <Field label={`Subtítulo (${data.subtitle.length}/300)`}>
        <TextArea value={data.subtitle} onChange={v => upd('subtitle', v)} rows={3} maxLength={300} placeholder="Atendimento de qualidade..." />
      </Field>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Badges ({data.badges.length})</label>
          <span className="text-xs text-gray-400">Chips exibidos abaixo do subtítulo</span>
        </div>
        {data.badges.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <IconPickerInline value={b.icon} onChange={icon => editBadge(i, { icon })} />
            <input
              type="text" value={b.label} onChange={e => editBadge(i, { label: e.target.value })} maxLength={60}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <button onClick={() => removeBadge(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text" value={badgeDraft} onChange={e => setBadgeDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBadge(); } }}
            placeholder="Ex: 5.0 no Google" maxLength={60}
            className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
          />
          <button onClick={addBadge} className="flex items-center gap-1 bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab 2: Consultórios ────────────────────────────────────────────────────────
function ConsultorioEditor({
  consultorio,
  onChange,
  onDelete,
}: {
  consultorio: ConsultorioItem;
  onChange: (updated: ConsultorioItem) => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const upd = <K extends keyof ConsultorioItem>(key: K, val: ConsultorioItem[K]) =>
    onChange({ ...consultorio, [key]: val });

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-primary shrink-0" />
          <span className="text-sm font-semibold text-gray-800">{consultorio.name || "Novo Consultório"}</span>
          {consultorio.city && <span className="text-xs text-gray-400">— {consultorio.city}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
            title="Excluir consultório"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nome do Consultório">
              <TextInput value={consultorio.name} onChange={v => upd('name', v)} placeholder="Consultório Paulista" maxLength={100} />
            </Field>
            <Field label="ID (slug)">
              <TextInput value={consultorio.consultorioId} onChange={v => upd('consultorioId', v)} placeholder="paulista" maxLength={50} />
            </Field>
          </div>

          <Field label="Endereço">
            <TextInput value={consultorio.address} onChange={v => upd('address', v)} placeholder="Av. Paulista, 807 - conj 1001" maxLength={200} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Cidade - UF">
              <TextInput value={consultorio.city} onChange={v => upd('city', v)} placeholder="São Paulo - SP" maxLength={80} />
            </Field>
            <Field label="CEP">
              <TextInput value={consultorio.cep} onChange={v => upd('cep', v)} placeholder="01311-100" maxLength={10} />
            </Field>
            <Field label="Complemento">
              <TextInput value={consultorio.complement} onChange={v => upd('complement', v)} placeholder="Edifício..." maxLength={100} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Telefone">
              <TextInput value={consultorio.phone} onChange={v => upd('phone', v)} placeholder="(11) 99999-0000" maxLength={20} />
            </Field>
            <Field label="Link Google Maps" hint="Cole o link do consultório no Google Maps. Ele será exibido como botão nas páginas de Consultórios e Contato.">
              <TextInput value={consultorio.googleMapsUrl} onChange={v => upd('googleMapsUrl', v)} placeholder="https://g.co/..." maxLength={300} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="URL de Agendamento" hint="Deixe vazio para usar o padrão do site">
              <TextInput value={consultorio.agendamentoUrl ?? ''} onChange={v => upd('agendamentoUrl', v)} placeholder="https://calendar.app.google/..." maxLength={500} />
            </Field>
            <Field label="Badge do Card" hint="Ex: Verificado, Novo, Destaque">
              <TextInput value={consultorio.badgeText ?? 'Verificado'} onChange={v => upd('badgeText', v)} placeholder="Verificado" maxLength={30} />
            </Field>
          </div>

          <PhotoGalleryEditor photos={consultorio.photos ?? []} onChange={v => upd('photos', v)} />
          <StringListEditor label="Horários" items={consultorio.horarios} onChange={v => upd('horarios', v)} />
          <StringListEditor label="Transporte" items={consultorio.transporte} onChange={v => upd('transporte', v)} />
          <StringListEditor label="Serviços" items={consultorio.servicos} onChange={v => upd('servicos', v)} />
          <StringListEditor label="Destaques" items={consultorio.destaces} onChange={v => upd('destaces', v)} />
        </div>
      )}
    </div>
  );
}

function ConsultoriosTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [items, setItems]   = useState<ConsultorioItem[]>(DEFAULT_CONSULTORIOS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'consultorios_data')?.value;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) setItems(parsed);
          } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updateItem = (i: number, updated: ConsultorioItem) => {
    setItems(prev => prev.map((c, idx) => idx === i ? updated : c));
    setDirty(true);
  };

  const deleteItem = (i: number) => {
    setItems(prev => prev.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const addItem = () => {
    const newItem: ConsultorioItem = {
      id: Date.now(),
      consultorioId: `consultorio-${Date.now()}`,
      name: "Novo Consultório",
      address: "",
      city: "",
      cep: "",
      complement: "",
      phone: "",
      googleMapsUrl: "",
      agendamentoUrl: "",
      badgeText: "Verificado",
      horarios: [],
      transporte: [],
      servicos: [],
      destaces: [],
      photos: [],
    };
    setItems(prev => [...prev, newItem]);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('consultorios_data', JSON.stringify(items));
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={MapPin} title="Consultórios" description="Gerencie os cards de cada consultório" />

      <div className="space-y-3">
        {items.map((c, i) => (
          <ConsultorioEditor
            key={c.id}
            consultorio={c}
            onChange={updated => updateItem(i, updated)}
            onDelete={() => deleteItem(i)}
          />
        ))}
      </div>

      <button
        onClick={addItem}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-brand-primary/30 hover:border-brand-primary/60 text-brand-primary text-sm font-medium rounded-xl py-3 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Adicionar Consultório
      </button>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab 3: Consulta Online ─────────────────────────────────────────────────────
function OnlineTab() {
  const { toast } = useToast();
  const [data, setData]   = useState<OnlineData>(DEFAULT_ONLINE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'consultorios_online')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_ONLINE, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof OnlineData>(key: K, val: OnlineData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const updateFeature = (i: number, field: keyof OnlineData['features'][0], v: string) => {
    const arr = [...data.features];
    arr[i] = { ...arr[i], [field]: v };
    upd('features', arr);
  };

  const addFeature = () => {
    upd('features', [...data.features, { icon: "Clock", title: "Novo Benefício", desc: "" }]);
  };

  const removeFeature = (i: number) => upd('features', data.features.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('consultorios_online', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Calendar} title="Consulta Online" description="Seção de atendimento online na página /consultorios" />

      <Field label="Ícone do Cabeçalho">
        <div className="flex items-center gap-2">
          <IconPickerInline value={data.headerIcon ?? 'Monitor'} onChange={v => upd('headerIcon', v)} />
          <span className="text-xs text-gray-400">Ícone exibido no painel verde</span>
        </div>
      </Field>
      <Field label="Título">
        <TextInput value={data.title} onChange={v => upd('title', v)} placeholder="Consulta Online" maxLength={80} />
      </Field>
      <Field label={`Descrição (${data.desc.length}/400)`}>
        <TextArea value={data.desc} onChange={v => upd('desc', v)} rows={3} maxLength={400} placeholder="Não pode se deslocar?..." />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Texto do Botão">
          <TextInput value={data.buttonText} onChange={v => upd('buttonText', v)} placeholder="Agendar Online" maxLength={50} />
        </Field>
        <Field label="URL do Botão" hint="Deixe vazio para usar o Google Calendar padrão">
          <TextInput value={data.buttonUrl} onChange={v => upd('buttonUrl', v)} placeholder="https://..." maxLength={500} />
        </Field>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Features ({data.features.length})</label>
          <span className="text-xs text-gray-400">Benefícios exibidos à direita</span>
        </div>
        {data.features.map((f, i) => (
          <div key={i} className="border border-gray-100 rounded-xl p-3 space-y-3">
            <div className="flex items-center gap-2">
              <IconPickerInline value={f.icon} onChange={v => updateFeature(i, 'icon', v)} />
              <input
                type="text" value={f.title} onChange={e => updateFeature(i, 'title', e.target.value)}
                placeholder="Título do benefício" maxLength={60}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
              />
              <button onClick={() => removeFeature(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
            <TextArea value={f.desc} onChange={v => updateFeature(i, 'desc', v)} rows={2} maxLength={200} placeholder="Descrição do benefício..." />
          </div>
        ))}
        <button
          onClick={addFeature}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-brand-primary/30 hover:border-brand-primary/60 text-brand-primary text-sm font-medium rounded-xl py-3 transition-colors"
        >
          <Plus className="w-4 h-4" /> Adicionar Feature
        </button>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab 4: Estatísticas ────────────────────────────────────────────────────────
function EstatisticasTab() {
  const { toast } = useToast();
  const [data, setData]   = useState<StatsData>(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'consultorios_stats')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_STATS, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof StatsData>(key: K, val: StatsData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const updateStat = (i: number, field: keyof StatItem, v: string) => {
    const arr = [...data.stats];
    arr[i] = { ...arr[i], [field]: v };
    upd('stats', arr);
  };

  const addStat = () => {
    upd('stats', [...data.stats, { value: "0", label: "Novo Número" }]);
  };

  const removeStat = (i: number) => upd('stats', data.stats.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('consultorios_stats', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Star} title="Estatísticas" description="Números exibidos na seção de estatísticas" />

      <Field label="Título da Seção">
        <TextInput value={data.sectionTitle} onChange={v => upd('sectionTitle', v)} placeholder="Números que Falam por Si" maxLength={80} />
      </Field>
      <Field label="Subtítulo da Seção">
        <TextInput value={data.sectionSubtitle} onChange={v => upd('sectionSubtitle', v)} placeholder="A confiança dos nossos pacientes em números" maxLength={150} />
      </Field>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Estatísticas ({data.stats.length})</label>
        </div>
        {data.stats.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text" value={s.value} onChange={e => updateStat(i, 'value', e.target.value)}
              placeholder="25+" maxLength={20}
              className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-bold text-center focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <input
              type="text" value={s.label} onChange={e => updateStat(i, 'label', e.target.value)}
              placeholder="Avaliações Google" maxLength={60}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <button onClick={() => removeStat(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          </div>
        ))}
        <button
          onClick={addStat}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-brand-primary/30 hover:border-brand-primary/60 text-brand-primary text-sm font-medium rounded-xl py-3 transition-colors"
        >
          <Plus className="w-4 h-4" /> Adicionar Estatística
        </button>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tabs nav ───────────────────────────────────────────────────────────────────
const TABS = [
  { id: "cabecalho",    label: "Cabeçalho" },
  { id: "consultorios", label: "Consultórios" },
  { id: "online",       label: "Consulta Online" },
  { id: "estatisticas", label: "Estatísticas" },
] as const;

type TabId = typeof TABS[number]['id'];

// ── Main export ────────────────────────────────────────────────────────────────
export function ConsultoriosManager() {
  const [activeTab, setActiveTab] = useState<TabId>("cabecalho");

  return (
    <div className="space-y-4">
      <PageVisibilityToggle pageKey="page_consultorios_visible" label="Página de Consultórios" />

      {/* Tab nav */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-max px-3 py-2 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-white text-brand-primary shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "cabecalho"    && <CabecalhoTab />}
      {activeTab === "consultorios" && <ConsultoriosTab />}
      {activeTab === "online"       && <OnlineTab />}
      {activeTab === "estatisticas" && <EstatisticasTab />}
    </div>
  );
}
